import { ref } from 'vue'

export function useAttachmentDownload() {
  const downloading = ref(false)
  const downloadError = ref(null)

  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    )
  }

  const isAndroid = () => {
    return /Android/i.test(navigator.userAgent)
  }

  const isTelegramWebView = () => {
    if (typeof window === 'undefined' || !window.Telegram || !window.Telegram.WebApp) {
      return false
    }

    const tgWebApp = window.Telegram.WebApp

    // Check if we're actually inside Telegram WebView
    // initData will only have content when actually inside Telegram, not just when SDK is loaded
    const hasInitData = tgWebApp.initData && tgWebApp.initData.length > 0

    // initDataUnsafe.user is the most reliable indicator - it only exists in real Telegram WebView
    const hasUser =
      tgWebApp.initDataUnsafe && tgWebApp.initDataUnsafe.user && tgWebApp.initDataUnsafe.user.id

    // Require both initData and user to be present for maximum reliability
    return hasInitData && hasUser
  }

  // Helper function to download via blob (fallback method)
  const downloadViaBlob = async (absoluteUrl, filename) => {
    const response = await fetch(absoluteUrl, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      let message = `Download failed (${response.status})`
      try {
        const body = await response.json()
        if (body?.errors?.length) {
          message = body.errors[0]
        }
      } catch {
        // non-JSON body
      }
      downloadError.value = message
      return
    }

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    link.style.display = 'none'
    link.tabIndex = -1
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up blob URL after a delay to ensure download starts
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl)
    }, 1000)
  }

  const downloadAttachment = async (url, filename) => {
    if (downloading.value) return

    downloadError.value = null

    try {
      downloading.value = true

      // Ensure URL is absolute
      const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`

      // Telegram WebView: Try downloadFile() first
      if (isTelegramWebView()) {
        const tgWebApp = window.Telegram.WebApp
        if (tgWebApp && typeof tgWebApp.downloadFile === 'function') {
          const version = tgWebApp.version
          const canUseDownloadFile = !version || parseFloat(version) >= 8.0

          if (canUseDownloadFile) {
            try {
              // Use the correct pattern: downloadFile({url, file_name}, callback)
              // Following Telegram example: https://github.com/revenkroz/telegram-web-app-bot-example
              tgWebApp.downloadFile({ url: absoluteUrl, file_name: filename }, function (result) {
                if (result) {
                  // Download started successfully
                  downloading.value = false
                } else {
                  // User declined - fall back to blob approach
                  downloadViaBlob(absoluteUrl, filename).finally(() => {
                    downloading.value = false
                  })
                }
              })
              // Don't set downloading.value = false here - wait for callback
              return
            } catch (error) {
              // downloadFile() failed - fall through to blob approach
            }
          }
        }

        // Fallback: Use blob approach if downloadFile() fails or isn't available
        await downloadViaBlob(absoluteUrl, filename)
        return
      }

      // Regular browser handling (non-Telegram WebView)
      if (isIOS()) {
        await downloadForIOS(absoluteUrl, filename)
      } else {
        await downloadViaBlob(absoluteUrl, filename)
      }
    } catch (error) {
      downloadError.value = 'Download failed. Please try again.'
    } finally {
      downloading.value = false
    }
  }

  // iOS-specific download fallback methods
  const downloadForIOS = async (url, filename) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      })

      if (!response.ok) {
        let message = `Download failed (${response.status})`
        try {
          const body = await response.json()
          if (body?.errors?.length) {
            message = body.errors[0]
          }
        } catch {
          // non-JSON body
        }
        downloadError.value = message
        return
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      // Try multiple iOS download methods
      const methods = [
        // Method 1: iframe
        () => {
          const iframe = document.createElement('iframe')
          iframe.style.display = 'none'
          iframe.src = blobUrl
          document.body.appendChild(iframe)
          setTimeout(() => document.body.removeChild(iframe), 1000)
        },
        // Method 2: form submission
        () => {
          const form = document.createElement('form')
          form.method = 'GET'
          form.action = blobUrl
          form.style.display = 'none'
          document.body.appendChild(form)
          form.submit()
          setTimeout(() => document.body.removeChild(form), 1000)
        },
        // Method 3: window.open
        () => {
          window.open(blobUrl, '_blank')
        },
        // Method 4: location.href
        () => {
          window.location.href = blobUrl
        },
        // Method 5: link.click (last resort)
        () => {
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = filename
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        },
      ]

      for (let i = 0; i < methods.length; i++) {
        try {
          methods[i]()
          // Clean up blob URL after a delay
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000)
          return
        } catch (error) {
          // Try next method
        }
      }

      downloadError.value = 'Failed to download file on iOS. Please try again.'
    } catch (error) {
      downloadError.value = 'Download failed. Please try again.'
    }
  }

  return {
    downloading,
    downloadError,
    downloadAttachment,
    isIOS,
    isAndroid,
    isTelegramWebView,
  }
}
